import {
    extractSelectionLimitWarning,
    MASSIVE_RESPONSE_WARNING_TOAST_OPTIONS,
} from '@/components/modal-answer-picked/modal-answer-picked-selection-warning';
import { buildMassAnswerAiPayload } from '@/components/modal-answer-picked/modal-answer-picked-ai-answer';
import {
    ModalAnswerPickedControllerBindings,
    UseModalAnswerPickedControllerArgs,
} from '@/components/modal-answer-picked/modal-answer-picked-controller.types';
import { useModalAnswerPickedPhotoPreview } from '@/components/modal-answer-picked/use-modal-answer-picked-photo-preview';
import { useModalAnswerPickedSelectionData } from '@/components/modal-answer-picked/use-modal-answer-picked-selection-data';
import { useModalAnswerPickedTemplates } from '@/components/modal-answer-picked/use-modal-answer-picked-templates';
import { useReviewsPageQueryContext } from '@/context/reviews-page.context';
import { useSelectedAutoRepliesModal } from '@/context/selected-auto-replies-modal.context';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SetStateAction } from 'react';
import { toast } from '@/ui/toast';
import { TOAST_IDS } from '@/ui/toast-ids';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useClientFeatureControls } from '@/context/client-feature-controls.context';
import { useRefreshClientAiQuota } from '@/hooks/use-refresh-client-ai-quota';
import {
    isReviewAiAnswerJobCanceledError,
    useCancelableReviewAiAnswerJob,
} from '@/hooks/use-cancelable-review-ai-answer-job';
import { isClientAiQuotaExhaustedAvailability } from '@/shared/client-ai-quota';
import { resolveClientFeatureControlDescriptor } from '@/shared/client-feature-control-policy';
import { formatMarketplaceApiErrorMessageFromUnknown } from '@/shared/errors/marketplace-api-error-message';
import { createClientLogger } from '@/client/logging/client-logger';
import { logOfferNotAcceptedMarketplacePolicyWarning } from '@/client/logging/offer-not-accepted-policy-logger';
import {
    createAiAnswerDraftSource,
    createHumanAnswerDraftSource,
    createTemplateAnswerDraftSource,
    resolveAnswerKindForSubmit,
    type AnswerDraftSource,
} from '@/shared/answer-draft-source';
import { resolveOperationId } from '@/shared/operation-id';
import { QUEUE_JOBS } from '@/constants/queue';

const VIRTUAL_ROW_HEIGHT = 190;
const VIRTUAL_OVERSCAN = 4;
const modalAnswerPickedLogger = createClientLogger({
    scope: 'modal-answer-picked',
});

export const useModalAnswerPickedController = ({
    isShown,
    setIsShown,
    setValue,
    value,
    accessToken,
    selection,
    transport,
    refetchActiveJobs,
}: UseModalAnswerPickedControllerArgs): ModalAnswerPickedControllerBindings => {
    const { reviewsApi } = useMarketplaceApi();
    const { openSelectedAutoRepliesModal } = useSelectedAutoRepliesModal();
    const { featureAvailability, featureControls } = useClientFeatureControls();
    const { refetchReviewsAndProducts } = useReviewsPageQueryContext();
    const refreshClientAiQuota = useRefreshClientAiQuota();
    const aiAnswerJob = useCancelableReviewAiAnswerJob({
        accessToken,
        jobName: QUEUE_JOBS.generateReviewAiAnswer,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAiGenerating, setIsAiGenerating] = useState(false);
    const [aiErrorText, setAiErrorText] = useState<string | null>(null);
    const [answerDraftSource, setAnswerDraftSource] = useState<AnswerDraftSource>(() => createHumanAnswerDraftSource());
    const [virtualScrollTop, setVirtualScrollTop] = useState(0);
    const [virtualViewportHeight, setVirtualViewportHeight] = useState(0);

    const listRef = useRef<HTMLDivElement | null>(null);
    const latestAiRequestIdRef = useRef(0);
    const latestValueRef = useRef(value);
    const isShownRef = useRef(isShown);

    const pickedReviewsCount = selection.pickedReviews.length;
    const selectionSessionId = selection.selectionSessionId;
    const aiAnswerAvailability = featureAvailability.aiAnswerGeneration;

    const {
        selectedReviewsForAnswer,
        setSelectedReviewsForAnswer,
        isSelectedReviewsLoading,
        selectedReviewsErrorText,
    } = useModalAnswerPickedSelectionData({
        isShown,
        accessToken,
        selectionSessionId,
        pickedReviewsCount,
    });

    useEffect(() => {
        latestValueRef.current = value;
    }, [value]);

    useEffect(() => {
        isShownRef.current = isShown;
    }, [isShown]);

    useEffect(() => {
        if (!isShown || !listRef.current) {
            return;
        }

        const updateViewportHeight = () => {
            setVirtualViewportHeight(listRef.current?.clientHeight || 0);
        };

        updateViewportHeight();
        window.addEventListener('resize', updateViewportHeight);

        return () => {
            window.removeEventListener('resize', updateViewportHeight);
        };
    }, [isShown, isSelectedReviewsLoading, selectedReviewsErrorText, selectedReviewsForAnswer.length]);

    useEffect(() => {
        if (!isShown) {
            return;
        }

        setVirtualScrollTop(0);
        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
    }, [isShown, selectedReviewsForAnswer.length]);

    useEffect(() => {
        if (!isShown) {
            void aiAnswerJob.cancelActive('dialog_closed');
            return;
        }

        setAiErrorText(null);
        setIsAiGenerating(false);
        setAnswerDraftSource(createHumanAnswerDraftSource());
    }, [aiAnswerJob, isShown]);

    const setIsShownWithAiCancellation = (nextIsShown: boolean) => {
        if (!nextIsShown) {
            void aiAnswerJob.cancelActive('dialog_closed');
        }

        setIsShown(nextIsShown);
    };

    const setValueWithAiCancellation = (nextValue: SetStateAction<string>) => {
        if (isAiGenerating) {
            void aiAnswerJob.cancelActive('draft_changed');
        }

        setValue(nextValue);
    };

    const photoPreview = useModalAnswerPickedPhotoPreview();
    const templatesBindings = useModalAnswerPickedTemplates({
        isShown,
        accessToken,
        value,
        setValue: setValueWithAiCancellation,
        onTemplateApplied: (templateText) => {
            void aiAnswerJob.cancelActive('template_applied');
            setAnswerDraftSource(createTemplateAnswerDraftSource(templateText));
        },
    });

    const onGenerateAiAnswer = async () => {
        const targetReview = selectedReviewsForAnswer[0];
        if (!targetReview || isAiGenerating || isSelectedReviewsLoading || isSubmitting) {
            return;
        }
        if (!aiAnswerAvailability.enabled) {
            setAiErrorText(aiAnswerAvailability.reason);
            return;
        }

        setAiErrorText(null);
        setIsAiGenerating(true);
        const draftBeforeGeneration = latestValueRef.current;
        const requestId = latestAiRequestIdRef.current + 1;
        latestAiRequestIdRef.current = requestId;
        const operationId = resolveOperationId(undefined, Date.now());

        try {
            const generatedAnswer = await aiAnswerJob.run({
                operationId,
                enqueue: (resolvedOperationId, { signal }) =>
                    reviewsApi.generateAiAnswer(
                        {
                            ...buildMassAnswerAiPayload(targetReview),
                            operationId: resolvedOperationId,
                        },
                        {
                            authToken: accessToken,
                            signal,
                        },
                    ),
            });

            if (requestId !== latestAiRequestIdRef.current) {
                return;
            }

            void refetchReviewsAndProducts().catch(() => undefined);

            if (!isShownRef.current) {
                return;
            }

            if (latestValueRef.current !== draftBeforeGeneration) {
                return;
            }

            setValue(generatedAnswer);
            setAnswerDraftSource(createAiAnswerDraftSource(generatedAnswer));
            setAiErrorText(null);
            await refreshClientAiQuota();
        } catch (error) {
            if (isReviewAiAnswerJobCanceledError(error)) {
                return;
            }

            if (requestId !== latestAiRequestIdRef.current || !isShownRef.current) {
                return;
            }

            logOfferNotAcceptedMarketplacePolicyWarning({
                logger: modalAnswerPickedLogger,
                message:
                    '[client.modal-answer-picked.offer-policy] Selected-review AI answer action blocked before offer acceptance.',
                error,
                context: {
                    action: 'modal_answer_picked.ai_answer.generate',
                    operationId,
                    reviewFeedItemId: targetReview.id,
                },
            });
            setAiErrorText(
                formatMarketplaceApiErrorMessageFromUnknown(error) ??
                    'Ошибка сервиса ИИ. Не удалось сгенерировать ответ',
            );
            await refreshClientAiQuota();
        } finally {
            if (requestId === latestAiRequestIdRef.current) {
                setIsAiGenerating(false);
            }
        }
    };

    const onAnswerReviews = async () => {
        if (!selectedReviewsForAnswer.length || !value.trim()) {
            return;
        }
        if (!featureControls.allowResponseSending) {
            toast.warning(resolveClientFeatureControlDescriptor('allowResponseSending').message);
            return;
        }

        void aiAnswerJob.cancelActive('answer_submitted');
        setAiErrorText(null);
        setIsSubmitting(true);

        try {
            const data = selectedReviewsForAnswer.map((review) => ({
                reviewFeedItemId: review.id,
                text: value,
                answerKind: resolveAnswerKindForSubmit({
                    source: answerDraftSource,
                    text: value,
                }),
            }));

            const response = await reviewsApi.createReviewAnswers(data, { authToken: accessToken });
            const limitWarning = extractSelectionLimitWarning(response.headers as Record<string, unknown>);
            if (limitWarning) {
                toast.warning(limitWarning, {
                    ...MASSIVE_RESPONSE_WARNING_TOAST_OPTIONS,
                    toastId: TOAST_IDS.answerPickCreateAnswersLimitWarning,
                });
            }
            const skipped = Array.isArray(response.data?.skipped) ? response.data.skipped : [];
            if (skipped.length) {
                const preview = skipped
                    .slice(0, 3)
                    .map((entry) => entry.reviewFeedItemId)
                    .join(', ');
                const suffix = skipped.length > 3 ? ', ...' : '';
                toast.warning(`Пропущены ${skipped.length} записей: ${preview}${suffix}`, {
                    ...MASSIVE_RESPONSE_WARNING_TOAST_OPTIONS,
                    toastId: TOAST_IDS.answerPickCreateAnswersSkipped,
                });
            }
            await refetchActiveJobs(accessToken);
            if (selectionSessionId) {
                await transport.enqueueReviewSelectionMutation(() =>
                    reviewsApi.clearReviewSelectionSession(selectionSessionId, {
                        authToken: accessToken,
                    }),
                );
            }

            setValue('');
            setAnswerDraftSource(createHumanAnswerDraftSource());
            setIsShownWithAiCancellation(false);
            selection.setPickedReviews?.([]);
            setSelectedReviewsForAnswer([]);
            toast.success('Ответы поставлены в очередь');
        } catch (error) {
            logOfferNotAcceptedMarketplacePolicyWarning({
                logger: modalAnswerPickedLogger,
                message:
                    '[client.modal-answer-picked.offer-policy] Bulk answer action blocked before offer acceptance.',
                error,
                context: {
                    action: 'modal_answer_picked.answer.create',
                    selectedReviewCount: selectedReviewsForAnswer.length,
                },
            });
            setAiErrorText(
                formatMarketplaceApiErrorMessageFromUnknown(error) ?? 'Не удалось поставить ответы в очередь',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const onOpenSelectedAutoReplies = () => {
        openSelectedAutoRepliesModal();
    };

    const canSubmit =
        !!value.trim() &&
        !!selectedReviewsForAnswer.length &&
        featureControls.allowResponseSending &&
        !isSubmitting &&
        !isSelectedReviewsLoading &&
        !isAiGenerating;
    const canGenerateAiAnswer =
        !!selectedReviewsForAnswer.length &&
        aiAnswerAvailability.enabled &&
        !isSelectedReviewsLoading &&
        !isSubmitting &&
        !isAiGenerating;
    const aiAnswerDisabledReason = !aiAnswerAvailability.enabled ? aiAnswerAvailability.reason : null;
    const isAiAnswerQuotaExhausted = isClientAiQuotaExhaustedAvailability(aiAnswerAvailability);

    const virtualizedRange = useMemo(() => {
        if (!selectedReviewsForAnswer.length) {
            return { start: 0, end: -1 };
        }

        const start = Math.max(Math.floor(virtualScrollTop / VIRTUAL_ROW_HEIGHT) - VIRTUAL_OVERSCAN, 0);
        const end = Math.min(
            selectedReviewsForAnswer.length - 1,
            Math.ceil((virtualScrollTop + virtualViewportHeight) / VIRTUAL_ROW_HEIGHT) + VIRTUAL_OVERSCAN,
        );

        return { start, end };
    }, [selectedReviewsForAnswer.length, virtualScrollTop, virtualViewportHeight]);

    const virtualizedReviews = useMemo(() => {
        if (virtualizedRange.end < virtualizedRange.start) {
            return [];
        }

        return selectedReviewsForAnswer.slice(virtualizedRange.start, virtualizedRange.end + 1);
    }, [selectedReviewsForAnswer, virtualizedRange.end, virtualizedRange.start]);

    return {
        listRef,
        setIsShown: setIsShownWithAiCancellation,
        setValue: setValueWithAiCancellation,
        isSelectedReviewsLoading,
        selectedReviewsErrorText,
        selectedReviewsForAnswer,
        isAiGenerating,
        aiErrorText,
        virtualizedReviews,
        virtualizedRangeStart: virtualizedRange.start,
        virtualizedTotalHeight: selectedReviewsForAnswer.length * VIRTUAL_ROW_HEIGHT,
        virtualRowHeight: VIRTUAL_ROW_HEIGHT,
        canSubmit,
        canGenerateAiAnswer,
        aiAnswerDisabledReason,
        isAiAnswerQuotaExhausted,
        onVirtualListScroll: setVirtualScrollTop,
        onGenerateAiAnswer,
        onAnswerReviews,
        onOpenSelectedAutoReplies,
        ...templatesBindings,
        ...photoPreview,
    };
};

export type { ModalAnswerPickedControllerBindings };
